import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import ReportToolbar from "../../components/report/ReportToolbar";
import RequestReport from "../../components/report/RequestReport";
import AppContext from "../../context/AppContext";
import { fetchReportData } from "../../service/Report";
import { FileText, Download, SearchX, Inbox } from "lucide-react";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import { getPermissionByScreenCode } from "../../utils/permissions";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import useDirection from "../../hooks/useDirection";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import TableScrollShell from "../../components/common/TableScrollShell";

const REPORT_REQUEST_ENABLED = false;

const useDebounce = (func, delay) => {
  const timeoutRef = useRef(null);
  const debouncedFunction = useCallback(
    (...args) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        func(...args);
      }, delay);
    },
    [func, delay]
  );
  return debouncedFunction;
};

const ReportList = () => {
  const [reportData, setReportData] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Number of rows per page
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [filterResetToken, setFilterResetToken] = useState(0);
  const { theme, selectedOrgName, langMode, selectedOrgId, setSelectedOrgName } =
    useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const reportsPerm = getPermissionByScreenCode("REPORTS");
  // Report requests only covered Vehicle/Inventory reports, which the backend
  // no longer generates, so the Request Report entry points stay hidden.
  // Existing reports can still be listed and downloaded.
  const canCreateReport = REPORT_REQUEST_ENABLED && Boolean(reportsPerm?.canCreate);
  const [loading, setLoading] = useState(false);
  const hasActiveFilters = Boolean(
    filters?.ReportType || filters?.UserType || filters?.Status || filters?.fromDate || filters?.toDate
  );
  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    debouncedLoadReportData();
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const tableHeaders = [
    translations[langMode].Organizations,
    translations[langMode].user,
    translations[langMode].userType,
    translations[langMode].reportType,
    translations[langMode].fromdate,
    translations[langMode].todate,
    translations[langMode].Status,
  ];

  const loadReportData = async () => {
    setLoading(true);
    const result = await fetchReportData(
      currentPage,
      pageSize,
      filters,
      selectedOrgName
    );
    setReportData(result.reportList);
    setTotalValue(result.totalCount);
    setLoading(false);
  };
  useEffect(() => {
    if (selectedOrgId) {
      setCurrentPage(0);
      setPageSize(10);
      loadReportData(selectedOrgId, currentPage, pageSize);
    }
  }, [selectedOrgId]);

  // Skip the very first run: the org-id effect above already fires an
  // equivalent load on mount, so without this guard both effects would
  // issue the same request on initial load. Every later filter/page/org-name
  // change still triggers the debounced reload as before.
  const isInitialFilterRunRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRunRef.current) {
      isInitialFilterRunRef.current = false;
      return;
    }
    debouncedLoadReportData();
  }, [filters, currentPage, pageSize, selectedOrgName]);

  useEffect(() => {
    const storedOrgName = localStorage.getItem("userDetails");
    if (storedOrgName) {
      localStorage.setItem("selectedOrgName", JSON.parse(storedOrgName)?.organization?.organizationName || "");
    }
  }, [])

  const handleReportRequestSuccess = () => {
    loadReportData();
  };
  const debouncedLoadReportData = useDebounce(loadReportData, 300);

  const handleDownloadClick = (url) => {
    window.open(url, "_blank");
  };
  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',

    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };
  return (
    <div
      className={`min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex flex-col overflow-y-auto sm:overflow-hidden ${theme === "dark"
        ? "dark-mode"
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
        }`}
    >
      <div
        className={`shrink-0 flex flex-col gap-3 mx-2 mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1
              className={`text-base font-bold text-slate-800 leading-tight sm:text-lg ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
            >
              {translations[langMode].report}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode]?.reportsSubtitle ?? "Request, track, and download reports for your organization."}
            </p>
          </div>

          <div className="flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto sm:justify-start sm:ml-auto">
            {canCreateReport && (
              <CommonButton colorIntent="primary"
                onClick={() => setOpen(true)}
                theme={theme}
                variant="primary"
                tooltipText={translations[langMode].requestonewreport}
                tooltipPosition="top"
                wrapperClassName="w-full sm:w-auto"
                className="w-full sm:w-auto !rounded-lg !h-10 !min-h-[40px] !px-3 !gap-2 justify-center"
              >
                <FileText size={16} className="shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap truncate">{translations[langMode].reportbtn}</span>
              </CommonButton>
            )}
          </div>
        </div>

        <ReportToolbar key={filterResetToken} onFiltersChange={setFilters} />
      </div>

      <div className="flex-1 min-h-0 w-full flex flex-col">
        <div
          className={`mx-2 mb-2 mt-2 flex min-h-[24rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1 ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
        >
          <div className="relative z-20 flex flex-wrap items-center justify-between gap-2 sm:gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
            <p className="flex items-center gap-2 text-xs text-slate-500 sm:gap-4">
              <span className="text-xs text-slate-500 whitespace-nowrap sm:hidden">
                {`${startIndex + 1}-${Math.min(endIndex, totalItems)} / ${totalItems}`}
              </span>
              <span className="hidden sm:inline whitespace-nowrap">
                {`${translations[langMode].showing || "Showing"} ${startIndex + 1}-${Math.min(endIndex, totalItems)} ${translations[langMode].of || "of"} ${totalItems}`}
              </span>
            </p>

            <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
              <div className="flex items-center gap-1.5">
                <label htmlFor="pageSize" className="hidden sm:inline whitespace-nowrap">
                  {translations[langMode].rows}
                </label>
                <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
              </div>
              <span className="pager-nav flex items-center gap-1">
                <PagerArrowButton
                  direction="prev"
                  isRtl={isRtl}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 0}
                  ariaLabel={translations[langMode].previousPage}
                />
                <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                  {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                </span>
                <PagerArrowButton
                  direction="next"
                  isRtl={isRtl}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  ariaLabel={translations[langMode].nextPage}
                />
              </span>
            </div>
          </div>

          {loading && <Spinner />}

          <TableScrollShell theme={theme} className="w-full flex-1 min-h-0" innerClassName="task-list-scrollbar">
            <table
              className={`w-full min-w-[920px] border-collapse text-xs text-center text-gray-500 ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
              dir={dir}
            >
              <thead
                className={`sticky top-0 z-10 border-b ${theme === "dark"
                  ? "bg-gray-800 text-gray-200 border-gray-700"
                  : theme === "high-contrast"
                    ? "high-contrast bg-gray-100"
                    : "border-slate-200 bg-slate-50 text-xs text-slate-700"
                  }`}
              >
                <tr>
                  <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center">
                    NO.
                  </th>
                  {tableHeaders.map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.map((report, index) => (
                  <tr
                    key={index}
                    className={`text-xs border-b border-slate-100 transition-colors duration-150 ${theme === "dark"
                      ? "dark-mode hover:bg-gray-800"
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : "bg-white hover:bg-slate-50/70"
                      }`}
                  >
                    <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center">
                      {startIndex + index + 1}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5" dir="auto">{report.organizationName}</td>
                    <td className="px-3 sm:px-4 py-2.5" dir="auto">{report.userId}</td>
                    <td className="px-3 sm:px-4 py-2.5">
                      <span
                        className={`inline-block px-3 py-1 rounded-lg font-semibold ${report.userType === "office_user"
                          ? theme === "dark"
                            ? "text-purple-300 bg-purple-600 bg-opacity-20"
                            : "text-purple-600 bg-purple-100 "
                          : report.userType === "admin"
                            ? theme === "dark"
                              ? "text-blue-300 bg-blue-600 bg-opacity-20"
                              : "text-blue-600 bg-blue-100 "
                            : "text-gray-600 bg-gray-200 "
                          }`}
                      >
                        {report.userType.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5" dir="auto">{report.reportType}</td>
                    <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">{formattedDate(report.fromDate)}</td>
                    <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">{formattedDate(report.toDate)}</td>
                    <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                      {report.status === 0 ? (
                        <button type="button" className="min-h-[36px] h-9 inline-flex items-center px-4 rounded-md text-sm text-gray-700 bg-gray-200 cursor-default">
                          {translations[langMode].Pending}
                        </button>
                      ) : (
                        <CommonButton colorIntent="primary"
                          onClick={() => handleDownloadClick(report.reportLink)}
                          variant="primary"
                          className="text-sm font-semibold"
                        >
                          <Download size={14} />
                          {translations[langMode].download}
                        </CommonButton>
                      )}
                    </td>
                  </tr>
                ))}
                {totalValue === 0 && !loading && (
                  <tr>
                    <td colSpan={tableHeaders.length + 1} className="py-16 h-[200px] align-middle">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                          {hasActiveFilters ? <SearchX size={22} /> : <Inbox size={22} />}
                        </div>
                        <div className="text-sm font-semibold text-slate-800">
                          {hasActiveFilters
                            ? (translations[langMode]?.noRecordsMatchSearch ?? "No records match your search")
                            : translations[langMode].NoReportDataavailble}
                        </div>
                        <div className="text-xs mt-1 max-w-xs text-slate-400">
                          {hasActiveFilters
                            ? (translations[langMode]?.tryAdjustingFilters ?? "Try adjusting your filters or search terms to find what you're looking for.")
                            : canCreateReport
                              ? (translations[langMode]?.getStartedRequestingReport ?? "Get started by requesting your first report.")
                              : null}
                        </div>
                        {hasActiveFilters ? (
                          <button data-button-intent="warning"
                            type="button"
                            onClick={() => setFilterResetToken((t) => t + 1)}
                            className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                          >
                            {translations[langMode]?.clearFilters ?? "Clear filters"}
                          </button>
                        ) : (
                          canCreateReport && (
                            <button
                              type="button"
                              onClick={() => setOpen(true)}
                              className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-blue-600"
                            >
                              {translations[langMode].reportbtn}
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScrollShell>
        </div>
      </div>

      {canCreateReport && (
        <RequestReport
          open={open}
          setOpen={setOpen}
          handleReportRequestSuccess={handleReportRequestSuccess}
        />
      )}
    </div>
  );
};
export default ReportList;
